import {
  ConditionFieldEnum,
  type BusinessSegmentConditions,
} from '@reach/shared-types/conditions';
import { partnerApi, type ApiResponse } from '../../../lib/api';
import { API_ENDPOINTS } from '../../../lib/constants';

export interface CreateSegmentDefinitionResponse {
  id: string;
  canonicalVersionId: string;
}

export interface CreateSegmentDefinitionParams {
  name: string;
  description: string;
  contents: BusinessSegmentConditions;
}

export interface GetSegmentDefinitionResponse {
  id: string;
  name: string;
  description: string | null;
  canonicalVersionId: string;
  contents: BusinessSegmentConditions;
  updatedAt: string | null;
}

export interface UpdateSegmentDefinitionParams {
  name?: string;
  description?: string;
  contents?: BusinessSegmentConditions;
}

export interface UpdateSegmentDefinitionResponse {
  id: string;
}

export interface FieldToExpose {
  name: string;
  description?: string;
  jsonPath: string;
  exposure: string;
}

export interface SchemaDefinitionWithFields {
  id: string;
  name: string;
  fields_to_expose?: FieldToExpose[] | null;
}

/**
 * Create a new segment definition from a template.
 * platformId is automatically derived from auth context.
 */
export async function createSegmentDefinitionFromTemplate(
  params: CreateSegmentDefinitionParams,
): Promise<CreateSegmentDefinitionResponse> {
  const response = await partnerApi.request<
    ApiResponse<CreateSegmentDefinitionResponse>
  >(API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS, {
    method: 'POST',
    body: JSON.stringify({
      name: params.name,
      contents: params.contents,
      description: params.description,
    }),
  });

  if (!response.success) {
    throw new Error(response.message || 'Failed to create segment');
  }

  return response.data;
}

/** Get a segment definition by canonical version ID. */
export async function getSegmentDefinition(
  canonicalVersionId: string,
): Promise<GetSegmentDefinitionResponse> {
  const response = await partnerApi.request<
    ApiResponse<GetSegmentDefinitionResponse>
  >(`${API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS}/${canonicalVersionId}`);

  return response.data;
}

/** Update a segment definition. */
export async function updateSegmentDefinition(
  canonicalVersionId: string,
  params: UpdateSegmentDefinitionParams,
): Promise<UpdateSegmentDefinitionResponse> {
  const response = await partnerApi.request<
    ApiResponse<UpdateSegmentDefinitionResponse>
  >(`${API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS}/${canonicalVersionId}`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });

  if (!response.success) {
    throw new Error(response.message || 'Failed to update segment');
  }

  return response.data;
}

/**
 * Default built-in fields that are always available for segment conditions
 * Derived from ConditionFieldEnum to stay in sync with backend
 */
const DEFAULT_FIELDS = ConditionFieldEnum.options;

/**
 * Fetch available fields from schema definitions
 * Returns a flat list of all field names that can be used in segment conditions
 * Includes both default built-in fields and custom fields from schema definitions
 */
export async function fetchAvailableFields(): Promise<string[]> {
  const response = await partnerApi.request<
    ApiResponse<SchemaDefinitionWithFields[]>
  >(API_ENDPOINTS.PARTNER.SCHEMA_DEFINITIONS);

  // Start with default fields
  const allFields: string[] = [...DEFAULT_FIELDS];

  // Add custom fields from schema definitions
  for (const schema of response.data) {
    if (schema.fields_to_expose && Array.isArray(schema.fields_to_expose)) {
      for (const field of schema.fields_to_expose) {
        if (field.name && !allFields.includes(field.name)) {
          allFields.push(field.name);
        }
      }
    }
  }

  return allFields.sort();
}

export interface PreviewRecipient {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface PreviewSegmentRecipientsResponse {
  recipients: PreviewRecipient[];
  pagination: {
    total: number;
    hasNextPage: boolean;
    cursor?: string;
  };
  estimatedTotal: number;
}

/**
 * Preview segment recipients for a given tenant
 * Uses the tenant-scoped endpoint with reach-tenant-id header
 */
export async function previewSegmentRecipients(
  tenantId: string,
  conditions: BusinessSegmentConditions,
  limit = 20,
): Promise<PreviewSegmentRecipientsResponse> {
  const response = await partnerApi.request<
    ApiResponse<PreviewSegmentRecipientsResponse>
  >(API_ENDPOINTS.AUTOMATIONS.PREVIEW_SEGMENT_RECIPIENTS, {
    method: 'POST',
    headers: {
      'reach-tenant-id': tenantId,
    },
    body: JSON.stringify({
      conditions,
      limit,
    }),
  });

  return response.data;
}
